import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageCircle,
  X,
  Send,
  Image as ImageIcon,
  Users,
  User,
  ChevronLeft,
  Loader2,
  Trash2,
  Shield,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StaffChat = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('group'); // 'group', 'dm', 'dm-chat'
  const [rooms, setRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [activeDmRoom, setActiveDmRoom] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const fileInputRef = useRef(null);

  // Draggable button state
  const [buttonPosition, setButtonPosition] = useState(() => {
    const saved = localStorage.getItem('staffChatButtonPosition');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { x: null, y: null };
      }
    }
    return { x: null, y: null };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);

  // Check if user is staff
  const isStaff = ['admin', 'chief_animator', 'animator', 'limited_editor'].includes(user?.role);

  // Handle touch/mouse events for dragging
  const handleDragStart = (e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const rect = buttonRef.current?.getBoundingClientRect();
    setDragStart({
      x: clientX - (rect?.left || 0),
      y: clientY - (rect?.top || 0)
    });
    setIsDragging(true);
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const buttonSize = 56; // w-14 = 56px
    const padding = 10;
    
    // Calculate new position with boundary constraints
    let newX = clientX - dragStart.x;
    let newY = clientY - dragStart.y;
    
    // Keep within viewport bounds
    newX = Math.max(padding, Math.min(window.innerWidth - buttonSize - padding, newX));
    newY = Math.max(padding, Math.min(window.innerHeight - buttonSize - padding, newY));
    
    setButtonPosition({ x: newX, y: newY });
  }, [isDragging, dragStart]);

  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // Save position to localStorage
      if (buttonPosition.x !== null) {
        localStorage.setItem('staffChatButtonPosition', JSON.stringify(buttonPosition));
      }
    }
  }, [isDragging, buttonPosition]);

  // Add and cleanup drag event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Handle button click (only if not dragging)
  const handleButtonClick = () => {
    if (!isDragging) {
      setIsOpen(!isOpen);
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch chat rooms
  const fetchRooms = useCallback(async () => {
    if (!isStaff) return;
    try {
      const response = await axios.get(`${API}/chat/rooms`);
      setRooms(response.data.rooms || []);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    }
  }, [isStaff]);

  // Fetch messages for a room
  const fetchMessages = useCallback(async (roomId) => {
    if (!isStaff) return;
    try {
      const response = await axios.get(`${API}/chat/messages/${roomId}?limit=50`);
      const msgs = response.data.messages || [];
      setMessages(msgs);
      setHasOlderMessages(msgs.length >= 50);
      setTimeout(scrollToBottom, 100);
      
      // Mark as read
      await axios.post(`${API}/chat/mark-read/${roomId}`);
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [isStaff]);

  // Load older messages (pagination)
  const loadOlderMessages = useCallback(async () => {
    if (!isStaff || messages.length === 0 || loadingOlder) return;
    const roomId = activeTab === 'dm-chat' ? activeDmRoom : 'staff_general';
    const oldestMsg = messages[0];
    const before = oldestMsg?.timestamp || oldestMsg?.sent_at;
    if (!before) return;
    
    setLoadingOlder(true);
    try {
      const response = await axios.get(`${API}/chat/messages/${roomId}?limit=50&before=${before}`);
      const olderMsgs = response.data.messages || [];
      if (olderMsgs.length === 0) {
        setHasOlderMessages(false);
      } else {
        setMessages(prev => [...olderMsgs, ...prev]);
        setHasOlderMessages(olderMsgs.length >= 50);
      }
    } catch (error) {
      console.error('Failed to load older messages:', error);
    }
    setLoadingOlder(false);
  }, [isStaff, messages, activeTab, activeDmRoom, loadingOlder]);


  // Fetch staff members for DM
  const fetchStaffMembers = useCallback(async () => {
    if (!isStaff) return;
    try {
      const response = await axios.get(`${API}/chat/staff-members`);
      setStaffMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  }, [isStaff]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isStaff) return;
    try {
      const response = await axios.get(`${API}/chat/unread-count`);
      setUnreadCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [isStaff]);

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    if (!isStaff || wsRef.current) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/chat/${token}`);
    
    ws.onopen = () => {
      console.log('Chat WebSocket connected');
      setWsConnected(true);
      // Subscribe to general room
      ws.send(JSON.stringify({ type: 'subscribe', room_id: 'staff_general' }));
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'new_message') {
        // Add message if we're viewing this room
        const currentRoom = activeTab === 'group' ? 'staff_general' : activeDmRoom?.id;
        if (data.room_id === currentRoom) {
          setMessages(prev => [...prev, data.message]);
          scrollToBottom();
        }
        // Update unread count
        fetchUnreadCount();
      }
      
      // Task — deleted messages disappear completely (Feb 2026).
      // Filter the message out entirely instead of showing a placeholder.
      // Also refresh unread count so the badge doesn't over-count when a
      // just-arrived message was the one being deleted.
      if (data.type === 'message_deleted') {
        setMessages(prev => prev.filter(msg => msg.id !== data.message_id));
        fetchUnreadCount();
      }
    };
    
    ws.onclose = () => {
      console.log('Chat WebSocket disconnected');
      setWsConnected(false);
      wsRef.current = null;
      // Reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  }, [isStaff, activeTab, activeDmRoom, fetchUnreadCount]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    const roomId = activeTab === 'group' ? 'staff_general' : activeDmRoom?.id;
    if (!roomId) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/chat/messages`, {
        room_id: roomId,
        message_type: 'text',
        content: newMessage.trim()
      });
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  // Delete message — hard delete on the server AND remove locally so the
  // row leaves no gap. The websocket broadcast will also arrive but
  // filtering is idempotent (nothing to remove second time).
  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  // Get role badge color and label
  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return { color: 'bg-red-100 text-red-700', label: 'Admin', icon: Shield };
      case 'chief_animator':
        return { color: 'bg-amber-100 text-amber-700', label: 'Chief', icon: Crown };
      case 'animator':
        return { color: 'bg-blue-100 text-blue-700', label: 'Animator', icon: null };
      case 'limited_editor':
        return { color: 'bg-purple-100 text-purple-700', label: 'Editor', icon: null };
      default:
        return { color: 'bg-gray-100 text-gray-700', label: role || 'Staff', icon: null };
    }
  };

  // Send image
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const roomId = activeTab === 'group' ? 'staff_general' : activeDmRoom?.id;
    if (!roomId) return;
    
    setSending(true);
    try {
      // Upload image first
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/chat/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Send as message
      await axios.post(`${API}/chat/messages`, {
        room_id: roomId,
        message_type: 'image',
        content: uploadRes.data.image_url
      });
    } catch (error) {
      toast.error('Failed to send image');
    }
    setSending(false);
  };

  // Start DM with staff member
  const startDm = async (memberId) => {
    try {
      const response = await axios.post(`${API}/chat/rooms/dm?other_user_id=${memberId}`);
      setActiveDmRoom(response.data);
      setActiveTab('dm-chat');
      fetchMessages(response.data.id);
      
      // Subscribe to this room
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'subscribe', room_id: response.data.id }));
      }
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  // Initial load
  useEffect(() => {
    if (isStaff) {
      fetchRooms();
      fetchStaffMembers();
      fetchUnreadCount();
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isStaff, fetchRooms, fetchStaffMembers, fetchUnreadCount, connectWebSocket]);

  // Load messages when opening chat or changing tabs
  useEffect(() => {
    if (isOpen && activeTab === 'group') {
      fetchMessages('staff_general');
    }
  }, [isOpen, activeTab, fetchMessages]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Don't render for non-staff
  if (!isStaff) return null;

  return (
    <>
      {/* Floating Button - Draggable on Mobile */}
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        className={`fixed z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary text-white shadow-lg hover:bg-primary/90 transition-colors flex items-center justify-center ${isDragging ? 'cursor-grabbing scale-110' : 'cursor-grab'}`}
        style={buttonPosition.x !== null ? {
          left: buttonPosition.x,
          top: buttonPosition.y,
          right: 'auto',
          bottom: 'auto'
        } : {
          // iOS: keep the FAB above the Home Indicator / Safari bottom bar.
          // `env(safe-area-inset-bottom)` is 0 on Android → no visual change.
          bottom: 'calc(1rem + env(safe-area-inset-bottom))',
          right: 'calc(1rem + env(safe-area-inset-right))',
        }}
        data-testid="staff-chat-button"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Panel - Responsive */}
      {isOpen && (
        <div 
          className="fixed z-50 bg-background border rounded-xl shadow-2xl flex flex-col overflow-hidden
            bottom-20 right-4
            w-[calc(100vw-2rem)] max-w-[380px]
            h-[calc(100dvh-6rem)] max-h-[500px]
            sm:bottom-24 sm:right-6 sm:w-96 sm:h-[500px]"
          style={{
            maxWidth: 'min(380px, calc(100vw - 2rem))',
            // iOS Safari: use dvh so URL-bar collapse doesn't clip the
            // message input; identical to vh on Android Chrome.
            maxHeight: 'min(500px, calc(100dvh - 6rem))',
            // Lift the whole panel above the Home Indicator on iOS.
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          data-testid="staff-chat-panel"
        >
          {/* Header — Task: mobile-friendly 44×44 close button always
              visible in the top-right, regardless of tab, so guests /
              animators can dismiss the chat with one thumb tap. */}
          <div className="bg-primary text-white px-3 py-2 flex items-center justify-between gap-2">
            {activeTab === 'dm-chat' && activeDmRoom ? (
              <>
                <button
                  onClick={() => setActiveTab('dm')}
                  className="min-w-11 min-h-11 flex items-center justify-center rounded-full hover:bg-white/20"
                  aria-label="Back to conversations"
                  data-testid="staff-chat-back-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-medium truncate flex-1 text-center">{activeDmRoom.other_user?.name || 'Direct Message'}</span>
              </>
            ) : (
              <>
                <h3 className="font-bold flex items-center gap-2 min-h-11 px-2">
                  <MessageCircle className="w-5 h-5" />
                  Staff Chat
                </h3>
                <div className="flex items-center gap-2 mr-auto ml-2">
                  <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-red-400'}`} aria-label={wsConnected ? 'Connected' : 'Reconnecting'} />
                </div>
              </>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="min-w-11 min-h-11 flex items-center justify-center rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
              aria-label="Close Staff Chat"
              data-testid="staff-chat-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          {activeTab !== 'dm-chat' && (
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab('group')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'group' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" /> Group
              </button>
              <button
                onClick={() => setActiveTab('dm')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'dm' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
                }`}
              >
                <User className="w-4 h-4 inline mr-1" /> Direct
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Group Chat / DM Chat View */}
            {(activeTab === 'group' || activeTab === 'dm-chat') && (
              <>
                <ScrollArea className="flex-1 p-3">
                  <div className="space-y-3">
                    {hasOlderMessages && messages.length >= 50 && (
                      <div className="text-center py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-muted-foreground"
                          onClick={loadOlderMessages}
                          disabled={loadingOlder}
                          data-testid="load-older-messages-btn"
                        >
                          {loadingOlder ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                          {loadingOlder ? 'Loading...' : 'Load older messages'}
                        </Button>
                      </div>
                    )}
                    {messages.filter((msg) => !msg.is_deleted).map((msg) => {
                      const isOwn = msg.sender_id === user?.id;
                      const canDelete = isOwn || user?.role === 'admin';
                      const roleBadge = getRoleBadge(msg.sender_role);
                      const RoleIcon = roleBadge.icon;
                      
                      return (
                        <div
                          key={msg.id}
                          className={`flex gap-2 group ${isOwn ? 'flex-row-reverse' : ''}`}
                          data-testid={`chat-message-${msg.id}`}
                        >
                          {/* Avatar */}
                          <div className="flex-shrink-0">
                            {msg.sender_photo ? (
                              <img 
                                src={msg.sender_photo} 
                                alt="" 
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                {msg.sender_name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                          
                          {/* Message */}
                          <div className={`max-w-[70%] ${isOwn ? 'items-end' : ''}`}>
                            {/* Sender info with role badge */}
                            <div className={`flex items-center gap-2 mb-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
                              <span className="text-xs text-muted-foreground font-medium">
                                {isOwn ? 'You' : msg.sender_name}
                              </span>
                              {!isOwn && msg.sender_role && (
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${roleBadge.color}`}>
                                  {RoleIcon && <RoleIcon className="w-2.5 h-2.5 mr-0.5" />}
                                  {roleBadge.label}
                                </Badge>
                              )}
                              <span className="text-[10px] text-muted-foreground/60">{formatTime(msg.timestamp)}</span>
                              
                              {/* Delete button - visible on hover */}
                              {canDelete && (
                                <button
                                  onClick={() => deleteMessage(msg.id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-500"
                                  title="Delete message"
                                  data-testid={`delete-message-${msg.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>

                            {/* Message content — deleted rows are removed
                                entirely upstream (filter above), so there is
                                no "This message was deleted" placeholder. */}
                            <div className={`rounded-lg p-2 ${
                              isOwn
                                ? 'bg-primary text-white'
                                : 'bg-muted'
                            }`}>
                              {msg.message_type === 'image' ? (
                                <img 
                                  src={msg.content} 
                                  alt="Shared" 
                                  className="rounded max-w-full max-h-48 object-contain cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => setFullscreenPhoto(msg.content)}
                                  loading="lazy"
                                />
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="p-3 border-t flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    className="flex-shrink-0"
                  >
                    <ImageIcon className="w-4 h-4" />
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    disabled={sending}
                    className="flex-1"
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={sending || !newMessage.trim()}
                    size="icon"
                    className="flex-shrink-0"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            )}

            {/* DM List View */}
            {activeTab === 'dm' && (
              <ScrollArea className="flex-1 p-3">
                <div className="space-y-2">
                  {staffMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => startDm(member.id)}
                      className="w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors flex items-center gap-3"
                    >
                      {member.photo ? (
                        <img 
                          src={member.photo} 
                          alt={member.name} 
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                          {member.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <p className="font-medium">{member.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {member.title || member.role}
                        </p>
                      </div>
                      {member.online && (
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                      )}
                    </button>
                  ))}
                  {staffMembers.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No other staff members</p>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      )}

      {/* Fullscreen Photo Viewer */}
      {fullscreenPhoto && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
          onClick={() => setFullscreenPhoto(null)}
          data-testid="photo-fullscreen"
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 z-10"
            onClick={() => setFullscreenPhoto(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={fullscreenPhoto}
            alt="Full view"
            className="max-w-[95vw] max-h-[95vh] object-contain"
            style={{ touchAction: 'pinch-zoom' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default StaffChat;
